import BaseComponent from 'scripts/BaseComponent.js';
import axios from 'axios';

export default class ProductCard extends BaseComponent {
  constructor(component) {
    super(component);
    this.debug = false;

    this.refs = {
      card: '.js-productCard',
      link: '.js-productCardLink',
      imageContainer: '.js-productCardImage',
      selectedColor: '.js-productCardColor',
      swatchBtn: '.js-productCardSwatch',
      priceContainer: '.js-priceContainer',
      swatchesContainer: '.js-productCardSwatches',
      hoverImg: '.js-productCardHoverImage',
      hoverVideo: '.js-productCardVideo',
      hoverVideoSrc: '.js-productCardVideoSrc',
      wishlist: '.js-wishlist-btn',
      quickShopBtn: '.js-quickShopBtn',
      quickShopPrice: '.js-quickShopPrice',
      compareAtPrice: '.js-compareAtPrice',
    };

    this.dom = {
      $component: component,
      $links: this.$getAll(this.refs.link),
      $imageContainer: this.$get(this.refs.imageContainer),
      $selectedColor: this.$get(this.refs.selectedColor),
      $swatchBtns: this.$getAll(this.refs.swatchBtn),
      $priceContainers: this.$getAll(this.refs.priceContainer),
      $swatchesContainer: this.$get(this.refs.swatchesContainer),
      $hoverImg: this.$get(this.refs.hoverImg),
      $hoverVideo: this.$get(this.refs.hoverVideo),
      $hoverVideoSrc: this.$get(this.refs.hoverVideoSrc),
      $wishlist: this.$get(this.refs.wishlist),
      $quickShopBtn: this.$get(this.refs.quickShopBtn),
      $quickShopPrice: this.$get(this.refs.quickShopPrice),
    };

    this.state = {
      activeColor: null,
      activeColorClass: 'is-selected',
      productSoldOut: false,
      soldOutClass: 'sold-out',
      observer: null,
      openQuickShopEvent: 'openquickshop',
    };

    this.mount();
  }

  mount() {
    const { dom, state } = this;
    const { asyncSwatches } = dom.$component.dataset;
    state.productSoldOut = dom.$priceContainers[0].classList.contains('product-sold-out');

    super.mount();

    if (asyncSwatches) {
      this.observe();
    } else {
      this.setSwatchListeners();
    }

    if (dom.$quickShopBtn) {
      this.setClickWithElement(dom.$quickShopBtn, this.openQuickShop);
    }

    if (dom.$hoverVideo && this.isDesktop()) {
      this.setOnEvent('mouseenter', dom.$hoverVideo, this.playHoverVideo);
      this.setOnEvent('mouseleave', dom.$hoverVideo, this.pauseHoverVideo);
    }
  }

  isDesktop() {
    const breakpoint = '(min-width: 1024px)';
    return window.matchMedia(breakpoint).matches;
  }

  observe() {
    const { dom, state } = this;

    state.observer = new IntersectionObserver(this.onIntersection.bind(this), {
      rootMargin: '250px',
    });
    state.observer.observe(dom.$component);
  }

  onIntersection([entry]) {
    const { state: { observer } } = this;

    if (entry.isIntersecting) {
      this.loadAsyncSwatches();
      observer.unobserve(entry.target);
    }
  }

  async loadAsyncSwatches() {
    const { dom } = this;
    const { handle, variantId } = dom.$component.dataset;

    try {
      const { data } = await axios.get(`/products/${handle}?variant=${variantId}&view=swatch-data`);
      const parser = new DOMParser();
      const page = parser.parseFromString(data, 'text/html');
      this.setCardSwatches(page);
      this.setHoverImage(page);
      dom.$swatchBtns = this.$getAll(this.refs.swatchBtn);
      this.setSwatchListeners();
    } catch (e) {
      console.log('error', e);
    }
  }

  setCardSwatches(html) {
    const { dom, refs } = this;
    const dataVariantTitle = html.querySelector(refs.selectedColor);
    const dataSwatches = html.querySelector(refs.swatchesContainer);
    if (dataVariantTitle) {
      dom.$selectedColor.innerHTML = dataVariantTitle.innerHTML;
    }
    if (dataSwatches) {
      dom.$swatchesContainer.innerHTML = dataSwatches.innerHTML;
    }
  }

  setHoverImage(html) {
    const { dom, refs } = this;
    const dataHoverImg = html.querySelector(refs.hoverImg);

    if (dataHoverImg) {
      dom.$hoverImg.innerHTML = dataHoverImg.innerHTML;
    }
  }

  setSwatchListeners() {
    const { dom } = this;

    this.setCurrentColor();
    this.setClickWithElement(dom.$swatchBtns, this.swatchClick);
  }

  setCurrentColor() {
    const { dom, state } = this;

    if (dom.$selectedColor) {
      state.activeColor = dom.$selectedColor.textContent.trim();
    }
  }

  swatchClick(swatch) {
    const { dom, state, refs } = this;
    const { productId } = dom.$component.dataset;
    const {
      id,
      sku,
      color,
      url,
      imageSrc,
      imageSrcsets,
      soldOut,
      price,
      compareAt,
    } = swatch.dataset;

    if (state.activeColor === color) return;

    const isSoldOut = soldOut ? soldOut === '1' : false;

    dom.$links.forEach((link) => link.setAttribute('href', url));
    dom.$selectedColor.textContent = color;
    this.setActiveImage(imageSrc, imageSrcsets);
    this.setActiveSwatch(swatch);
    this.setSoldOutState(isSoldOut);

    if (dom.$quickShopBtn) {
      this.setQuickShopBtn(id, sku, price, compareAt, isSoldOut);
    }

    state.activeColor = color;

    dom.$wishlist.dataset.variantId = id;
    // eslint-disable-next-line no-underscore-dangle
    window._swat.initializeActionButtons(`${refs.card}-${productId}`);
  }

  setActiveImage(imageSrc, imageSrcsets) {
    const { dom } = this;

    dom.$imageContainer.querySelector('img').src = imageSrc;
    imageSrcsets.split('|').forEach((srcsetString) => {
      const [media, srcset] = srcsetString.split('*');
      dom.$imageContainer.querySelector(`source[media="${media}"]`).srcset = srcset;
    });
  }

  setActiveSwatch(selectedSwatch) {
    const { state, refs } = this;
    const currentActiveSwatch = this.$get(`${refs.swatchBtn}[data-color="${state.activeColor}"]`);

    currentActiveSwatch.classList.remove(state.activeColorClass);
    selectedSwatch.classList.add(state.activeColorClass);
  }

  setSoldOutState(soldOut) {
    const { dom, state } = this;

    if (soldOut && !state.productSoldOut) {
      dom.$priceContainers.forEach((elem) => elem.classList.add(state.soldOutClass));
    } else {
      dom.$priceContainers.forEach((elem) => elem.classList.remove(state.soldOutClass));
    }
  }

  setQuickShopBtn(id, sku, price, compareAt, isSoldOut) {
    const { dom, state } = this;

    dom.$quickShopPrice.textContent = price;
    dom.$compareAtPrice.textContent = compareAt;
    dom.$quickShopBtn.setAttribute('data-variant-id', id);
    dom.$quickShopBtn.setAttribute('data-variant-sku', sku);

    if (isSoldOut && !this.state.productSoldOut) {
      dom.$quickShopBtn.classList.add(state.soldOutClass);
    } else {
      dom.$quickShopBtn.classList.remove(state.soldOutClass);
    }
  }

  openQuickShop(btn) {
    const { state } = this;
    const {
      productHandle,
      variantId,
      variantSku,
      productTitle,
    } = btn.dataset;

    this.dispatch(state.openQuickShopEvent, {
      handle: productHandle,
      id: variantId,
      sku: variantSku,
      title: productTitle,
    });
  }

  playHoverVideo(e) {
    e.stopPropagation();
    const {
      dom: {
        $hoverVideo,
        $hoverVideoSrc,
      },
    } = this;

    if (!$hoverVideoSrc.hasAttribute('src')) {
      this.loadVideo();
      return;
    }

    $hoverVideo.play();
  }

  pauseHoverVideo(e) {
    e.stopPropagation();
    const { dom: { $hoverVideo } } = this;

    $hoverVideo.pause();
  }

  loadVideo() {
    const {
      dom: {
        $hoverVideo,
        $hoverVideoSrc,
      },
    } = this;

    const { src } = $hoverVideoSrc.dataset;
    $hoverVideoSrc.setAttribute('src', src);
    $hoverVideo.load();
    $hoverVideo.play();
  }
}
